import React, { useState, useEffect, createContext, useContext } from 'react';
import { LogOut, Eye, EyeOff, Home, Briefcase, AlertCircle } from 'lucide-react';

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) setUser(JSON.parse(savedUser));
    setLoading(false);
  }, []);

  const login = (cpf, password) => {
    const users = JSON.parse(localStorage.getItem('users') || '{}');
    const userData = users[cpf];
    if (userData && userData.password === password) {
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      return true;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('currentUser');
  };

  return <AuthContext.Provider value={{ user, login, logout, loading }}>{children}</AuthContext.Provider>;
};

const LoginPage = () => {
  const { login } = useAuth();
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!cpf || !password) {
      setError('Preencha todos os campos');
      setLoading(false);
      return;
    }
    if (login(cpf, password)) {
      setLoading(false);
    } else {
      setError('CPF ou senha incorretos');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="inline-block bg-white rounded-full p-4 mb-6 shadow-lg">
            <Briefcase size={48} className="text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Office Finance</h1>
          <p className="text-blue-100 text-lg">Gestão Financeira</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Bem-vindo</h2>
          <p className="text-gray-600 mb-8">Faça login para continuar</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">CPF</label>
              <input type="text" value={cpf} onChange={(e) => setCpf(e.target.value.replace(/\D/g, ''))} placeholder="000.000.000-00" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" maxLength="11" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Senha</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Digite sua senha" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-600">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded"><AlertCircle size={18} className="inline mr-2" /><span className="text-red-700">{error}</span></div>}

            <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 rounded-lg hover:shadow-lg disabled:opacity-70">
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700 mb-2"><strong>Teste:</strong></p>
            <p className="text-sm text-gray-700">CPF: 07046798923</p>
            <p className="text-sm text-gray-700">Senha: 13012002Rrc</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem(`transactions_${user.cpf}`);
    if (saved) setTransactions(JSON.parse(saved));
  }, [user]);

  const typeLabels = {gastoFixo: '💰 Gasto Fixo', cartao: '💳 Cartão', ganhos: '💵 Ganhos', saldoCCo: '📊 Saldo', patrimonio: '🏠 Patrimônio', divida: '⚠️ Dívida'};

  const totals = {
    gastoFixo: transactions.filter(t => t.type === 'gastoFixo').reduce((sum, t) => sum + t.amount, 0),
    cartao: transactions.filter(t => t.type === 'cartao').reduce((sum, t) => sum + t.amount, 0),
    ganhos: transactions.filter(t => t.type === 'ganhos').reduce((sum, t) => sum + t.amount, 0),
    saldoCCo: transactions.filter(t => t.type === 'saldoCCo').reduce((sum, t) => sum + t.amount, 0),
    patrimonio: transactions.filter(t => t.type === 'patrimonio').reduce((sum, t) => sum + t.amount, 0),
    divida: transactions.filter(t => t.type === 'divida').reduce((sum, t) => sum + t.amount, 0)
  };

  const totalSaida = totals.gastoFixo + totals.cartao;
  const totalEntrada = totals.ganhos + totals.saldoCCo;
  const saldo = totalEntrada - totalSaida;

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(value);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Office Finance</h1>
            <p className="text-blue-100">Olá, {user.name}</p>
          </div>
          <button onClick={logout} className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
            <LogOut size={20} />
            Sair
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <h3 className="text-green-100 font-semibold mb-4">Total Entrada</h3>
              <p className="text-3xl font-bold">{formatCurrency(totalEntrada)}</p>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <h3 className="text-red-100 font-semibold mb-4">Total Saída</h3>
              <p className="text-3xl font-bold">{formatCurrency(totalSaida)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <h3 className="font-semibold opacity-80 mb-4">Saldo</h3>
              <p className="text-3xl font-bold">{formatCurrency(saldo)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Categorias</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(totals).map(([key, value]) => (
                <div key={key} className="p-4 rounded-lg bg-gray-50 border">
                  <p className="text-sm font-semibold mb-1">{typeLabels[key]}</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(value)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  const { user, loading } = useAuth();
  
  useEffect(() => {
    const users = JSON.parse(localStorage.getItem('users') || '{}');
    if (!users['07046798923']) {
      users['07046798923'] = {cpf: '07046798923', password: '13012002Rrc', name: 'Raphael'};
      localStorage.setItem('users', JSON.stringify(users));
    }
  }, []);
  
  if (loading) return <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center"><p className="text-white text-lg">Carregando...</p></div>;
  return user ? <Dashboard /> : <LoginPage />;
}

export default function AppWithAuth() {
  return <AuthProvider><App /></AuthProvider>;
}
